import { useState, useEffect } from "react";

/**
 * Custom hook that returns the current scroll position
 * @returns number indicating the current scroll position from the top
 */
export function useScrollPosition(): number {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      setScrollPosition(position);
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Call handler right away to initialize state
    handleScroll();

    // Clean up event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return scrollPosition;
}

/**
 * Custom hook that calculates the scroll percentage
 * @returns number indicating scroll percentage (0-100)
 */
export function useScrollPercentage(): number {
  const [scrollPercentage, setScrollPercentage] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      const scrollTop = window.scrollY;

      // Calculate percentage
      const scrolled = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollPercentage(Math.min(Math.max(scrolled, 0), 100));
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Call handler right away to initialize state
    handleScroll();

    // Clean up event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return scrollPercentage;
}
